import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Divider, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { TextInput } from '@/modules/common/components/inputs';

import { MAX_LIST_HEIGHT } from './constants';
import { AddRow } from './AddRow';
import { OptionRow } from './OptionRow';
import { normalizeText, pruneRecord, removeKey } from './utils';
import { OptionsEditorPopoverShell } from './OptionsEditorPopoverShell';
import type { TextOptionsEditorPopoverProps } from './types';

function TextOptionsEditorPopoverImpl({
  disabled,
  ariaLabel,
  icon,
  title,
  width,
  maxWidth,
  anchorOrigin,
  transformOrigin,
  onInputFocus,
  onInputBlur,
  options,
  onUpdateOption,
  onDeleteOption,
  onAddOption,
  addPlaceholder,
}: TextOptionsEditorPopoverProps) {
  const { t } = useTranslation('interface');

  const [newText, setNewText] = useState('');
  const [drafts, setDrafts] = useState<Record<string, string>>({});

  // Keep a ref so commit handlers don't need to depend on drafts.
  const draftsRef = useRef(drafts);
  useEffect(() => {
    draftsRef.current = drafts;
  }, [drafts]);

  const reset = useCallback(() => {
    setNewText('');
    setDrafts({});
  }, []);

  // Whenever the list of options changes, delete any local draft edits that belong to options that no longer exist.
  useEffect(() => {
    const ids = new Set(options.map((x) => x.id));
    // plz fix me
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setDrafts((prev) => pruneRecord(prev, ids));
  }, [options]);

  const duplicateCounts = useMemo(() => {
    const counts = new Map<string, number>();
    options.forEach(({ id, label }) => {
      const effective = drafts[id] ?? label;
      const key = normalizeText(effective);
      if (!key) return;
      counts.set(key, (counts.get(key) ?? 0) + 1);
    });
    return counts;
  }, [drafts, options]);

  const newTextIsDuplicate = useMemo(() => {
    const trimmed = newText.trim();
    if (!trimmed) return false;
    const key = normalizeText(trimmed);
    return (duplicateCounts.get(key) ?? 0) > 0;
  }, [duplicateCounts, newText]);

  const canAdd = useMemo(() => {
    if (disabled) return false;
    const trimmed = newText.trim();
    return trimmed.length > 0 && !newTextIsDuplicate;
  }, [disabled, newText, newTextIsDuplicate]);

  const commitDraft = useCallback(
    async (id: string, originalLabel: string) => {
      const draft = draftsRef.current[id];
      if (draft === undefined) return;

      const nextLabel = draft.trim();
      if (!nextLabel) return;

      const dupKey = normalizeText(nextLabel);
      const dupCount = duplicateCounts.get(dupKey) ?? 0;
      if (dupCount > 1) return;

      if (nextLabel === originalLabel) {
        setDrafts((prev) => removeKey(prev, id));
        return;
      }

      await onUpdateOption(id, nextLabel);
      setDrafts((prev) => removeKey(prev, id));
    },
    [duplicateCounts, onUpdateOption],
  );

  const addOption = useCallback(async () => {
    if (!canAdd) return;
    const label = newText.trim();
    if (!label) return;

    await onAddOption(label);
    setNewText('');
  }, [canAdd, newText, onAddOption]);

  const handleBlur = useCallback(
    (id: string, label: string) => async () => {
      try {
        await commitDraft(id, label);
      } finally {
        onInputBlur?.();
      }
    },
    [commitDraft, onInputBlur],
  );

  return (
    <OptionsEditorPopoverShell
      disabled={disabled}
      ariaLabel={ariaLabel}
      icon={icon}
      title={title}
      width={width}
      maxWidth={maxWidth}
      anchorOrigin={anchorOrigin}
      transformOrigin={transformOrigin}
      onClose={reset}
    >
      <Stack gap={1} sx={{ width: '100%' }}>
        <Stack
          gap={1}
          sx={{ width: '100%', maxHeight: MAX_LIST_HEIGHT, overflowY: 'auto', pr: 0.5 }}
        >
          {options.map(({ id, label }) => {
            const effective = drafts[id] ?? label;
            const key = normalizeText(effective);
            const duplicate = (duplicateCounts.get(key) ?? 0) > 1;
            const empty = effective.trim().length === 0;
            const showError = !disabled && (duplicate || empty);

            let helperText: string | undefined;
            if (showError) {
              helperText = empty
                ? t('select_options_editor.required', 'Required')
                : t('select_options_editor.duplicate', 'Duplicate');
            }

            return (
              <OptionRow
                key={id}
                disabled={disabled}
                onRemove={() => {
                  setDrafts((prev) => removeKey(prev, id));
                  onDeleteOption(id);
                }}
              >
                <TextInput
                  value={effective}
                  onChange={(value) => setDrafts((prev) => ({ ...prev, [id]: value }))}
                  disabled={disabled}
                  sx={{ width: '100%' }}
                  error={showError}
                  helperText={helperText}
                  onFocus={onInputFocus}
                  onBlur={handleBlur(id, label)}
                  errorType='floating'
                />
              </OptionRow>
            );
          })}

          {options.length === 0 && (
            <Typography variant='caption' sx={{ opacity: 0.7 }}>
              {t('select_options_editor.no_options', 'No options')}
            </Typography>
          )}
        </Stack>

        <Divider />

        <AddRow
          disabled={disabled}
          value={newText}
          onChange={setNewText}
          placeholder={addPlaceholder}
          canAdd={canAdd}
          showDuplicateError={newTextIsDuplicate}
          onAdd={addOption}
          onInputFocus={onInputFocus}
          onInputBlur={onInputBlur}
        />
      </Stack>
    </OptionsEditorPopoverShell>
  );
}

export const TextOptionsEditorPopover = memo(TextOptionsEditorPopoverImpl);
